import { PRICE_TABLE_URL } from 'consts/api';
import { showError } from 'helpers/handleError';
import { cloneDeep, isEmpty } from 'lodash';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSave } from 'redux/store/StoreCached';
import services from 'services';

//* GUIDE:
//**************************************
//* Check services of API              *
//* Check parseBodyToRequest function  *
//* Check checkConditionPass function  *
//**************************************

/**
 * @returns {{
 *  data: {price: number, service: string, unit: string}[],
 *  loading: boolean,
 *  refetching: boolean,
 *  error: string | any,
 *  refetch: () => Promise<void>,
 *  refetchWithLoading: () => Promise<void>,
 *  setData: Dispatch<SetStateAction<any[]>>,
 * }}
 */
let priceDataCached = [];
const useGetPriceTable = ({ isTrigger = true, cachedKey = '' } = {}) => {
  const save = useSave();
  const controller = useRef(new AbortController());
  const [data, setData] = useState(priceDataCached);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refetching, setRefetching] = useState(false);

  useEffect(() => {
    if (cachedKey) {
      save(cachedKey, { data, loading });
    }

    return () => {
      save(cachedKey, undefined);
    };
  }, [save, cachedKey, data, loading]);

  //! Function
  const checkConditionPass = useCallback((response) => {
    //* Check condition of response here to set data
    if (response?.data) {
      setData(response?.data);
      priceDataCached = cloneDeep(response?.data || []);
    }
  }, []);

  const fetch = useCallback(() => {
    if (!isTrigger || !isEmpty(priceDataCached)) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const response = await services.get(PRICE_TABLE_URL, {
          signal: controller.current.signal,
        });

        resolve(response);
      } catch (error) {
        setError(error);
        reject(error);
      }
    });
  }, [isTrigger]);

  //* Refetch implicity (without changing loading state)
  const refetch = useCallback(async () => {
    try {
      setRefetching(true);
      const response = await fetch();
      checkConditionPass(response);
    } catch (error) {
      showError(error);
    } finally {
      setRefetching(false);
    }
  }, [fetch, checkConditionPass]);

  //* Refetch with changing loading state
  const refetchWithLoading = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch();
      checkConditionPass(response);
      setLoading(false);
    } catch (error) {
      if (!!error?.isCanceled) {
        return;
      }
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [fetch, checkConditionPass]);

  useEffect(() => {
    let shouldSetData = true;
    controller.current = new AbortController();

    const fetchAPI = async () => {
      try {
        setLoading(true);
        const response = await fetch();
        if (shouldSetData) {
          checkConditionPass(response);
          setLoading(false);
        }
      } catch (error) {
        showError(error);
        setLoading(false);
      }
    };

    fetchAPI();

    return () => {
      controller.current.abort();
      shouldSetData = false;
    };
  }, [fetch]);

  return { data, loading, error, refetching, refetch, refetchWithLoading, setData };
};

export default useGetPriceTable;
